import { useState, useRef, useEffect } from 'react';
import { useAppStore } from '../store';
import {
    Send,
    Cloud,
    ChevronDown,
    Loader2,
    Lightbulb
} from 'lucide-react';
import { AVAILABLE_MODELS } from '../../worker/types';

const EXAMPLE_PROMPTS = [
    'Create a beautiful landing page for a SaaS product',
    'Build a todo app with add, complete, and delete features',
    'Make a weather dashboard with a search input',
    'Design a user profile card component',
    'Create a pricing table with three tiers',
];

export function PromptInput() {
    const {
        isGenerating,
        generationProgress,
        selectedModel,
        setSelectedModel,
        startGeneration,
        completeGeneration,
        errorGeneration,
        updateGenerationProgress,
        project,
        addFile
    } = useAppStore();

    const [prompt, setPrompt] = useState('');
    const [showModels, setShowModels] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Initialize with default model
    useEffect(() => {
        if (!selectedModel) {
            const defaultModel = AVAILABLE_MODELS.find(m => m.isDefault) || AVAILABLE_MODELS[0];
            setSelectedModel(defaultModel);
        }
    }, []);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setShowModels(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Auto-resize textarea
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
        }
    }, [prompt]);

    const handleSubmit = async () => {
        if (!prompt.trim() || isGenerating) return;

        startGeneration();

        try {
            // Get current file context and settings
            const { files: currentFiles, apiKey } = useAppStore.getState();
            const fileContext = Array.from(currentFiles.values()).map(f =>
                `File: ${f.path}\nContent:\n${f.content}`
            );

            const response = await fetch('/api/ai/generate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Project-Id': project?.id || ''
                },
                body: JSON.stringify({
                    prompt: prompt.trim(),
                    modelId: selectedModel?.id,
                    projectId: project?.id,
                    context: fileContext,
                    apiKey,
                }),
            });

            if (!response.ok) {
                throw new Error('Generation failed');
            }

            // Read streaming response
            const reader = response.body?.getReader();
            const decoder = new TextDecoder();
            let fullResponse = '';

            while (reader) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value, { stream: true });
                fullResponse += chunk;
                updateGenerationProgress(chunk);
            }

            // Flush any remaining bytes
            fullResponse += decoder.decode();

            // Parse the response into files
            const parseRes = await fetch('/api/ai/parse', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Project-Id': project?.id || ''
                },
                body: JSON.stringify({ response: fullResponse }),
            });

            const parseData = await parseRes.json() as { files?: Array<{ path: string; content: string }> };
            const generatedFiles = parseData.files;

            if (generatedFiles && generatedFiles.length > 0) {
                // Batch save to server
                await fetch('/api/files/batch', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        projectId: project?.id,
                        files: generatedFiles
                    }),
                });

                // Update local state
                completeGeneration(generatedFiles.map((f: { path: string; content: string }) => ({ ...f, type: 'file' })));
            } else {
                // Handle fallback for truncated or single-block responses
                // Strip markdown fences and language headers if they exist
                let cleanContent = fullResponse
                    .replace(/^```(?:\w+)?\n/, '') // Remove starting fence
                    .replace(/```$/, '')          // Remove ending fence
                    .replace(/^\/\/ filepath:.*\n/, '') // Remove filepath comment
                    .trim();

                if (cleanContent) {
                    addFile('src/App.tsx', cleanContent);
                    completeGeneration([{ path: 'src/App.tsx', content: cleanContent, type: 'file' }]);
                }
            }

            setPrompt('');
        } catch (error) {
            console.error('Generation error:', error);
            errorGeneration(error instanceof Error ? error.message : 'Generation failed');
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
    };

    const handleExampleClick = (example: string) => {
        setPrompt(example);
        textareaRef.current?.focus();
    };

    return (
        <div className="border-t border-border bg-background-secondary">
            {/* Example prompts */}
            {!isGenerating && prompt.length === 0 && (
                <div className="px-4 py-2 border-b border-border flex items-center gap-2 overflow-x-auto">
                    <Lightbulb className="w-4 h-4 text-amber-400 flex-shrink-0" />
                    <span className="text-xs text-text-muted flex-shrink-0">Try:</span>
                    {EXAMPLE_PROMPTS.map((example, i) => (
                        <button
                            key={i}
                            onClick={() => handleExampleClick(example)}
                            className="px-3 py-1 text-xs rounded-full bg-surface hover:bg-surface-hover
                         text-text-secondary hover:text-text-primary transition-colors
                         whitespace-nowrap flex-shrink-0"
                        >
                            {example}
                        </button>
                    ))}
                </div>
            )}

            {/* Generation progress */}
            {isGenerating && (
                <div className="px-4 py-3 border-b border-border">
                    <div className="flex items-center gap-2 mb-2">
                        <Loader2 className="w-4 h-4 text-accent-primary animate-spin" />
                        <span className="text-sm text-accent-primary font-medium">
                            {generationProgress ? 'Generating...' : `${selectedModel?.name || 'AI'} is thinking...`}
                        </span>
                    </div>
                    {generationProgress ? (
                        <div className="bg-background rounded-lg p-3 max-h-32 overflow-y-auto">
                            <pre className="text-xs text-text-secondary font-mono whitespace-pre-wrap">
                                {generationProgress.slice(-500)}
                            </pre>
                        </div>
                    ) : (
                        <div className="bg-background rounded-lg p-3 flex items-center gap-3">
                            <div className="flex gap-1">
                                <div className="w-2 h-2 rounded-full bg-accent-primary animate-pulse" style={{ animationDelay: '0ms' }} />
                                <div className="w-2 h-2 rounded-full bg-accent-primary animate-pulse" style={{ animationDelay: '150ms' }} />
                                <div className="w-2 h-2 rounded-full bg-accent-primary animate-pulse" style={{ animationDelay: '300ms' }} />
                            </div>
                            <span className="text-xs text-text-muted">Analyzing your request and generating code...</span>
                        </div>
                    )}
                </div>
            )}

            {/* Input area */}
            <div className="p-4">
                <div className="flex items-end gap-3">
                    {/* Model selector */}
                    <div ref={dropdownRef} className="relative">
                        <button
                            onClick={() => setShowModels(!showModels)}
                            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-surface hover:bg-surface-hover
                         text-sm text-text-secondary transition-colors"
                            disabled={isGenerating}
                        >
                            <Cloud className="w-4 h-4 text-accent-primary" />
                            <span className="max-w-[100px] truncate">{selectedModel?.name || 'Select Model'}</span>
                            <ChevronDown className="w-4 h-4" />
                        </button>

                        {showModels && (
                            <div className="absolute bottom-full left-0 mb-2 w-72 bg-surface-hover border border-border
                              rounded-lg shadow-xl overflow-hidden animate-fade-in z-50">
                                {AVAILABLE_MODELS.map((model) => (
                                    <button
                                        key={model.id}
                                        onClick={() => {
                                            setSelectedModel(model);
                                            setShowModels(false);
                                        }}
                                        className={`w-full text-left px-4 py-3 hover:bg-surface transition-colors
                                ${selectedModel?.id === model.id ? 'bg-accent-primary/10' : ''}`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="font-medium text-text-primary text-sm">{model.name}</span>
                                            <span className="text-xs text-text-muted">{model.provider}</span>
                                        </div>
                                        <p className="text-xs text-text-secondary mt-1">{model.description}</p>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Text input */}
                    <div className="flex-1 relative">
                        <textarea
                            ref={textareaRef}
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Vibe something into existence on Cloudflare... (e.g., 'Build a real-time analytics dashboard')"
                            disabled={isGenerating}
                            rows={1}
                            className="w-full px-4 py-3 rounded-xl bg-surface border border-border
                         focus:border-accent-primary focus:ring-2 focus:ring-accent-primary/20
                         text-text-primary placeholder-text-muted resize-none
                         transition-all duration-200 disabled:opacity-50"
                            style={{ minHeight: '48px' }}
                        />
                    </div>

                    {/* Submit button */}
                    <button
                        onClick={handleSubmit}
                        disabled={!prompt.trim() || isGenerating}
                        className="p-3 rounded-xl bg-gradient-to-r from-accent-primary to-accent-secondary
                       hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed
                       transition-all duration-200 shadow-glow"
                    >
                        {isGenerating ? (
                            <Loader2 className="w-5 h-5 text-white animate-spin" />
                        ) : (
                            <Send className="w-5 h-5 text-white" />
                        )}
                    </button>
                </div>

                {/* Hint */}
                <p className="mt-2 text-xs text-text-muted text-center">
                    Press Enter to generate • Shift+Enter for new line
                </p>
            </div>
        </div>
    );
}
